import { defineStore } from 'pinia';
import { useDbStore } from '@/stores/dbStore';


export const useBasicResourcesStore = defineStore('basicResources', {
  actions: {
    createCompositionResource(compositionData) {
      const compositionResource = {
        resourceType: 'Composition',
        id: compositionData.id,
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_ERP_Composition|1.1.0']
        },
        extension: [
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_FOR_Legal_basis',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_STATUSKENNZEICHEN',
              code: compositionData.legalBasis
            }
          }
        ],
        status: 'final',
        type: {
          coding: [
            {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_FORMULAR_ART',
              code: compositionData.formType
            }
          ]
        },
        subject: {
          reference: `${compositionData.patientId}`
        },
        date: compositionData.date,
        author: [
          {
            reference: `${compositionData.practitionerId}`,
            type: 'Practitioner'
          },
          {
            type: 'Device',
            identifier: {
              system: 'https://fhir.kbv.de/NamingSystem/KBV_NS_FOR_Pruefnummer',
              value: 'Y/400/2107/36/999'
            }
          }
        ],
        title: compositionData.title,
        custodian: {
          reference: `${compositionData.organizationId}`
        },
        section: [
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'Prescription'
                }
              ]
            },
            entry: [
              {
                reference: `${compositionData.medicationRequestId}`
              }
            ]
          },
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'Coverage'
                }
              ]
            },
            entry: [
              {
                reference: `${compositionData.coverageId}`
              }
            ]
          }
        ]
      }
    
      return compositionResource
    },

    createPatientResource(patientData) {
      const patientResource = {
        resourceType: 'Patient',
        id: patientData.uuid,
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_FOR_Patient|1.1.0']
        },
        identifier: [
          {
            type: {
              coding: [
                {
                  system: 'http://fhir.de/CodeSystem/identifier-type-de-basis',
                  code: 'GKV'
                }
              ]
            },
            system: 'http://fhir.de/sid/gkv/kvid-10',
            value: patientData.kvid
          }
        ],
        name: [
          {
            use: 'official',
            prefix: patientData.title,
            family: patientData.surname,
            given: [patientData.givenName]
          }
        ],
        gender: patientData.gender.toLowerCase(),
        birthDate: patientData.dateOfBirth ? new Date(patientData.dateOfBirth).toISOString().split('T')[0] : '',
        address: [
          {
            type: 'both',
            line: [patientData.streetAddress.street],
            city: patientData.streetAddress.city,
            postalCode: patientData.streetAddress.postalcode
          }
        ]
      }

      return patientResource
    },

    createPractitionerResource(practitionerData) {
      const practitionerResource = {
        resourceType: 'Practitioner',
        id: practitionerData.uuid,
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_FOR_Practitioner|1.1.0']
        },
        identifier: [
          {
            type: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
                  code: 'LANR'
                }
              ]
            },
            system: 'https://fhir.kbv.de/NamingSystem/KBV_NS_Base_ANR',
            value: practitionerData.lanr
          }
        ],
        name: [
          {
            use: 'official',
            family: practitionerData.surname,
            given: [practitionerData.givenName]
          }
        ],
        qualification: [
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_FOR_Qualification_Type',
                  code: practitionerData.qualificationType
                }
              ]
            }
          },
          {
            code: {
              text: practitionerData.qualificationType
            }
          }
        ]
      }

      return practitionerResource
    },

    createOrganizationResource(organizationData) {
      const organizationResource = {
        resourceType: 'Organization',
        id: organizationData.uuid,
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_FOR_Organization|1.1.0']
        },
        identifier: [
          {
            type: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
                  code: 'BSNR'
                }
              ]
            },
            system: 'https://fhir.kbv.de/NamingSystem/KBV_NS_Base_BSNR',
            value: organizationData.bsnr
          }
        ],
        name: organizationData.name,
        telecom: [
          {
            system: 'phone',
            value: organizationData.telephone
          }
        ],
        address: [
          {
            extension: [
              {
                url: 'http://hl7.org/fhir/StructureDefinition/iso21090-ADXP-streetName',
                valueString: organizationData.street
              },
              {
                url: 'http://hl7.org/fhir/StructureDefinition/iso21090-ADXP-houseNumber',
                valueString: organizationData.houseNumber
              }
            ],
            type: 'both',
            city: organizationData.city,
            postalCode: organizationData.postalcode,
            country: organizationData.landCode
          }
        ]
      };

      return organizationResource;
    },


    createCoverageResource(patientData) {
      const coverageResource = {
        resourceType: 'Coverage',
        id: patientData.insuranceInformations.uuid,
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_FOR_Coverage|1.1.0']
        },
        extension: [
          {
            url: 'http://fhir.de/StructureDefinition/gkv/besondere-personengruppe',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_PERSONENGRUPPE',
              code: '00'
            }
          }, 
          {
            url: 'http://fhir.de/StructureDefinition/gkv/dmp-kennzeichen',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_DMP',
              code: '00'
            }
          },
          {
            url: 'http://fhir.de/StructureDefinition/gkv/wop',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_ITA_WOP',
              code: '00'
            }
          },
          {
            url: 'http://fhir.de/StructureDefinition/gkv/versichertenart',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_VERSICHERTENSTATUS',
              code: '1'
            }
          }
        ],
        status: 'active',
        type: {
          coding: [
            {
              system: 'http://fhir.de/CodeSystem/versicherungsart-de-basis',
              code: patientData.insuranceInformations.insuranceType
            }
          ]
        },
        beneficiary: {
          reference: `${patientData.patientId}`
        },
        payor: [
          {
            identifier: {
              system: 'http://fhir.de/sid/arge-ik/iknr',
              value: patientData.insuranceInformations.billingInsuranceId
            },
            display: patientData.insuranceInformations.insuranceName
          }
        ]
      };

      return coverageResource
    },

    createFHIRBundle(resources) {
      const dbStore = useDbStore();
      const bundle = {
        resourceType: 'Bundle',
        id: crypto.randomUUID(),
        meta: {
          profile: ['https://fhir.kbv.de/StructureDefinition/KBV_PR_ERP_Bundle|1.1.0']
        },
        identifier: {
          system: 'https://gematik.de/fhir/erp/NamingSystem/GEM_ERP_NS_PrescriptionId',
          value: dbStore.createTaskResponse.prescriptionId
        },
        type: 'document',
        timestamp: new Date().toISOString(),
        entry: []
      }

      const sortedResources = [
        ...resources.filter(r => r.resourceType === 'Composition'),
        ...resources.filter(r => r.resourceType === 'MedicationRequest'),
        ...resources.filter(r => r.resourceType === 'Medication'),
        ...resources.filter(r => r.resourceType === 'Patient'),
        ...resources.filter(r => r.resourceType === 'Practitioner'),
        ...resources.filter(r => r.resourceType === 'Organization'),
        ...resources.filter(r => r.resourceType !== 'Composition' && r.resourceType !== 'MedicationRequest' && r.resourceType !== 'Medication' && r.resourceType !== 'Patient' && r.resourceType !== 'Practitioner' && r.resourceType !== 'Organization')
      ]

      sortedResources.forEach((resource, index) => {
        const entry = {
          fullUrl: `${resource.id}`,
          resource: resource
        }
        bundle.entry.push(entry)

        this.updateReferences(bundle, resource, index)
      })

      return bundle
    },

    updateReferences(bundle, resource) {
      if (resource.resourceType === 'MedicationRequest') {
        const patientIndex = bundle.entry.findIndex(entry => entry.resource.resourceType === 'Patient')
        const practitionerIndex = bundle.entry.findIndex(entry => entry.resource.resourceType === 'Practitioner')
        const coverageIndex = bundle.entry.findIndex(entry => entry.resource.resourceType === 'Coverage')

        if (patientIndex !== -1) {
          resource.subject = { reference: bundle.entry[patientIndex].fullUrl }
        }

        if (practitionerIndex !== -1) {
          resource.requester = { reference: bundle.entry[practitionerIndex].fullUrl }
        }

        if (coverageIndex !== -1) {
          resource.insurance = [{ reference: bundle.entry[coverageIndex].fullUrl }]
        }
      }
    },

    createCompositionHilfsmittelResource(compositionData) {
      const compositionResource = {
        resourceType: 'Composition',
        id: compositionData.id,
        meta: {
          profile: ['https://fhir.carelogic.de/StructureDefinition/CL_PR_ERP_Composition_HiMi|0.2.0']
        },
        extension: [
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_FOR_Legal_basis',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_STATUSKENNZEICHEN',
              code: compositionData.legalBasis
            }
          }
        ],
        status: 'final',
        type: {
          coding: [
            {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_FORMULAR_ART',
              code: compositionData.musterType
            }
          ]
        },
        subject: {
          reference: `${compositionData.patientId}`
        },
        date: compositionData.date,
        author: [
          {
            reference: `${compositionData.practitionerId}`,
            type: 'Practitioner'
          },
          {
            type: 'Device',
            identifier: {
              system: 'https://fhir.kbv.de/NamingSystem/KBV_NS_FOR_Pruefnummer',
              value: 'Y/400/2107/36/999'
            }
          }
        ],
        title: 'elektronische Hilfsmittelverordnung',
        custodian: {
          reference: `${compositionData.organizationId}`
        },
        section: [
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'PrescriptionHilfsmittel'
                }
              ]
            },
            entry: [
              {
                reference: `${compositionData.deviceRequestId}`
              }
            ]
          },
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'Coverage'
                }
              ]
            },
            entry: [
              {
                reference: `${compositionData.coverageId}`
              }
            ]
          }
        ]
      }

      return compositionResource
    },


    createHilfsmittelFHIRBundle(resources) {
      const dbStore = useDbStore();
      const bundle = {
        resourceType: 'Bundle',
        id: crypto.randomUUID(),
        meta: {
          profile: ['https://fhir.carelogic.de/StructureDefinition/CL_PR_ERP_Bundle_HiMi|0.2.0']
        },
        identifier: {
          system: 'https://gematik.de/fhir/erp/NamingSystem/GEM_ERP_NS_PrescriptionId',
          value: dbStore.createTaskResponse.prescriptionId
        },
        type: 'document',
        timestamp: new Date().toISOString(),
        entry: []
      }

      const sortedResources = [
        ...resources.filter(r => r.resourceType === 'Composition'),
        ...resources.filter(r => r.resourceType === 'DeviceRequest'),
        ...resources.filter(r => r.resourceType !== 'Composition' && r.resourceType !== 'DeviceRequest')
      ]

      sortedResources.forEach((resource, index) => {
        const entry = {
          fullUrl: `${resource.id}`,
          resource: resource
        }
        bundle.entry.push(entry)

        this.updateReferencesHilfsmittel(bundle, resource, index)
      })

      return bundle
    },


    updateReferencesHilfsmittel(bundle, resource) {
      if (resource.resourceType === 'DeviceRequest') {
        const patientIndex = bundle.entry.findIndex(entry => entry.resource.resourceType === 'Patient')
        const practitionerIndex = bundle.entry.findIndex(entry => entry.resource.resourceType === 'Practitioner')
        const coverageIndex = bundle.entry.findIndex(entry => entry.resource.resourceType === 'Coverage')

        if (patientIndex !== -1) {
          resource.subject = { reference: bundle.entry[patientIndex].fullUrl }
        }

        if (practitionerIndex !== -1) {
          resource.requester = { reference: bundle.entry[practitionerIndex].fullUrl }
        }

        if (coverageIndex !== -1) {
          resource.insurance = [{ reference: bundle.entry[coverageIndex].fullUrl }]
        }
      }
    },

  }
  
});